import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    getBookingMeetingPoints,
    createBookingMeetingPoint,
    updateBookingMeetingPoint,
    deleteBookingMeetingPoint,
    uploadBookingMeetingPointImage,
    getProviders,
    getTariffs
} from '../api';

const PRESET_AIRPORTS = [
    // Chile
    { code: 'SCL', name: 'Aeropuerto Internacional Arturo Merino Benítez (Santiago)', country: 'Chile', keywords: ['santiago', 'pudahuel', 'arturo merino', 'merino benitez', 'region metropolitana', 'valparaiso', 'vina del mar'] },
    { code: 'CJC', name: 'Aeropuerto Internacional El Loa (Calama)', country: 'Chile', keywords: ['calama', 'el loa', 'san pedro de atacama'] },
    { code: 'ANF', name: 'Aeropuerto Andrés Sabella (Antofagasta)', country: 'Chile', keywords: ['antofagasta', 'andres sabella', 'cerro moreno'] },
    { code: 'IQQ', name: 'Aeropuerto Internacional Diego Aracena (Iquique)', country: 'Chile', keywords: ['iquique', 'diego aracena', 'alto hospicio'] },
    { code: 'LSC', name: 'Aeródromo La Florida (La Serena)', country: 'Chile', keywords: ['la serena', 'aerodromo la florida', 'coquimbo', 'ovalle', 'valle del elqui'] },
    { code: 'CPO', name: 'Aeródromo Desierto de Atacama (Copiapó)', country: 'Chile', keywords: ['copiapo', 'desierto de atacama', 'caldera', 'bahia inglesa'] },
    { code: 'ARI', name: 'Aeropuerto Internacional Chacalluta (Arica)', country: 'Chile', keywords: ['arica', 'chacalluta'] },
    { code: 'CCP', name: 'Aeropuerto Carriel Sur (Concepción)', country: 'Chile', keywords: ['concepcion', 'carriel sur', 'talcahuano', 'biobio', 'chillan'] },
    { code: 'ZCO', name: 'Aeropuerto Internacional La Araucanía (Temuco)', country: 'Chile', keywords: ['temuco', 'la araucania', 'pucon', 'villarrica'] },
    { code: 'ZAL', name: 'Aeródromo Pichoy (Valdivia)', country: 'Chile', keywords: ['valdivia', 'pichoy'] },
    { code: 'ZOS', name: 'Aeródromo Cañal Bajo Carlos Hott Siebert (Osorno)', country: 'Chile', keywords: ['osorno', 'canal bajo'] },
    { code: 'PMC', name: 'Aeropuerto Internacional El Tepual (Puerto Montt)', country: 'Chile', keywords: ['puerto montt', 'el tepual', 'puerto varas', 'frutillar'] },
    { code: 'MHC', name: 'Aeródromo Mocopulli (Chiloé / Castro)', country: 'Chile', keywords: ['chiloe', 'mocopulli', 'castro', 'ancud', 'dalcahue'] },
    { code: 'BBA', name: 'Aeródromo Balmaceda (Coyhaique / Aysén)', country: 'Chile', keywords: ['balmaceda', 'coyhaique', 'aysen'] },
    { code: 'PNT', name: 'Aeródromo Teniente Julio Gallardo (Puerto Natales)', country: 'Chile', keywords: ['puerto natales', 'julio gallardo', 'torres del paine'] },
    { code: 'PUQ', name: 'Aeropuerto Internacional Pdte. Carlos Ibáñez del Campo (Punta Arenas)', country: 'Chile', keywords: ['punta arenas', 'carlos ibanez', 'magallanes'] },

    // República Dominicana
    { code: 'PUJ', name: 'Aeropuerto Internacional de Punta Cana', country: 'Dominican Republic', keywords: ['punta cana', 'bavaro', 'uvero alto', 'cap cana', 'macao'] },
    { code: 'SDQ', name: 'Aeropuerto Internacional Las Américas (Santo Domingo)', country: 'Dominican Republic', keywords: ['santo domingo', 'las americas', 'boca chica', 'juan dolio', 'samana'] },
    { code: 'JBQ', name: 'Aeropuerto Internacional La Isabela (Santo Domingo)', country: 'Dominican Republic', keywords: ['la isabela', 'higuero', 'joaquin balaguer'] },
    { code: 'LRM', name: 'Aeropuerto Internacional de La Romana', country: 'Dominican Republic', keywords: ['la romana', 'casa de campo', 'bayahibe'] },
    { code: 'POP', name: 'Aeropuerto Internacional Gregorio Luperón (Puerto Plata)', country: 'Dominican Republic', keywords: ['puerto plata', 'gregorio luperon', 'sosua', 'cabarete'] },

    // Perú
    { code: 'LIM', name: 'Aeropuerto Internacional Jorge Chávez (Lima)', country: 'Peru', keywords: ['lima', 'jorge chavez', 'callao', 'miraflores', 'san isidro', 'barranco'] },
    { code: 'CUZ', name: 'Aeropuerto Internacional Alejandro Velasco Astete (Cusco)', country: 'Peru', keywords: ['cusco', 'cuzco', 'velasco astete', 'valle sagrado', 'ollantaytambo'] },
    { code: 'AQP', name: 'Aeropuerto Internacional Rodríguez Ballón (Arequipa)', country: 'Peru', keywords: ['arequipa', 'rodriguez ballon'] },

    // Argentina
    { code: 'EZE', name: 'Aeropuerto Internacional Ministro Pistarini (Ezeiza - Buenos Aires)', country: 'Argentina', keywords: ['ezeiza', 'ministro pistarini', 'pistarini', 'buenos aires', 'caba', 'palermo', 'recoleta', 'microcentro'] },
    { code: 'AEP', name: 'Aeroparque Jorge Newbery (Buenos Aires)', country: 'Argentina', keywords: ['aeroparque', 'jorge newbery', 'buenos aires', 'caba', 'palermo', 'recoleta', 'microcentro'] },
    { code: 'MDZ', name: 'Aeropuerto Internacional El Plumerillo (Mendoza)', country: 'Argentina', keywords: ['mendoza', 'el plumerillo', 'plumerillo', 'lujan de cuyo', 'maipu', 'valle de uco'] },
    { code: 'COR', name: 'Aeropuerto Internacional Ing. Ambrosio Taravella (Córdoba)', country: 'Argentina', keywords: ['cordoba', 'ambrosio taravella', 'taravella', 'pajas blancas'] },
    { code: 'BRC', name: 'Aeropuerto Internacional Teniente Luis Candelaria (Bariloche)', country: 'Argentina', keywords: ['bariloche', 'san carlos de bariloche', 'nahuel huapi'] },
    { code: 'IGR', name: 'Aeropuerto Internacional Cataratas del Iguazú', country: 'Argentina', keywords: ['puerto iguazu', 'cataratas del iguazu'] },

    // Brasil
    { code: 'GRU', name: 'Aeropuerto Internacional de São Paulo-Guarulhos', country: 'Brasil', keywords: ['guarulhos', 'sao paulo'] },
    { code: 'CGH', name: 'Aeropuerto de São Paulo-Congonhas', country: 'Brasil', keywords: ['congonhas', 'sao paulo'] },
    { code: 'VCP', name: 'Aeropuerto Internacional de Viracopos / Campinas (VCP / BCP)', country: 'Brasil', keywords: ['viracopos', 'biracopos', 'bcp', 'vcp', 'campinas', 'sao paulo'] },
    { code: 'GIG', name: 'Aeropuerto Internacional de Galeão (Río de Janeiro)', country: 'Brasil', keywords: ['galeao', 'rio de janeiro', 'copacabana', 'ipanema', 'barra da tijuca', 'buzios', 'angra dos reis'] },
    { code: 'SDU', name: 'Aeropuerto Santos Dumont (Río de Janeiro)', country: 'Brasil', keywords: ['santos dumont', 'rio de janeiro', 'copacabana', 'ipanema'] },
    { code: 'FLN', name: 'Aeropuerto Internacional Hercílio Luz (Florianópolis)', country: 'Brasil', keywords: ['florianopolis', 'hercilio luz', 'balneario camboriu'] },

    // Colombia / Uruguay / México / Panamá / España
    { code: 'BOG', name: 'Aeropuerto Internacional El Dorado (Bogotá)', country: 'Colombia', keywords: ['bogota', 'el dorado'] },
    { code: 'MDE', name: 'Aeropuerto Internacional José María Córdova (Medellín)', country: 'Colombia', keywords: ['medellin', 'rionegro', 'jose maria cordova'] },
    { code: 'CTG', name: 'Aeropuerto Internacional Rafael Núñez (Cartagena)', country: 'Colombia', keywords: ['cartagena de indias', 'rafael nunez'] },
    { code: 'MVD', name: 'Aeropuerto Internacional de Carrasco (Montevideo)', country: 'Uruguay', keywords: ['montevideo', 'carrasco', 'punta del este'] },
    { code: 'CUN', name: 'Aeropuerto Internacional de Cancún', country: 'Mexico', keywords: ['cancun', 'riviera maya', 'playa del carmen', 'tulum'] },
    { code: 'MEX', name: 'Aeropuerto Internacional Benito Juárez (Ciudad de México)', country: 'Mexico', keywords: ['ciudad de mexico', 'cdmx', 'benito juarez'] },
    { code: 'PTY', name: 'Aeropuerto Internacional de Tocumen (Panamá)', country: 'Panama', keywords: ['ciudad de panama', 'tocumen'] },
    { code: 'MAD', name: 'Aeropuerto Adolfo Suárez Madrid-Barajas', country: 'Spain', keywords: ['madrid', 'barajas'] },
    { code: 'BCN', name: 'Aeropuerto Josep Tarradellas Barcelona-El Prat', country: 'Spain', keywords: ['barcelona', 'el prat'] }
];

// Mapa exacto de ID de Base Operativa EmiGo -> Aeropuertos habilitados en esa base
const BASE_ID_TO_AIRPORTS = {
    1:  ['SCL'],                // Santiago / RM
    2:  ['PUJ', 'LRM'],         // Punta Cana / La Romana
    3:  ['EZE', 'AEP'],         // Buenos Aires
    4:  ['MDZ'],                // Mendoza
    5:  ['MDZ'],                // Mendoza
    6:  ['SDQ', 'JBQ'],         // Santo Domingo
    7:  ['LIM'],                // Lima
    8:  ['BOG'],                // Bogotá
    9:  ['ARI'],                // Arica
    10: ['IQQ'],                // Iquique
    11: ['ANF'],                // Antofagasta
    12: ['CPO'],                // Copiapó
    13: ['LSC'],                // La Serena
    14: ['SCL'],                // Valparaíso / Viña del Mar
    15: ['SCL'],                // Rancagua
    16: ['SCL'],                // Talca
    17: ['CCP'],                // Chillán
    18: ['CCP'],                // Concepción
    19: ['ZCO'],                // Temuco
    20: ['ZAL'],                // Valdivia
    21: ['PMC'],                // Puerto Montt
    22: ['BBA'],                // Balmaceda / Coyhaique
    23: ['PUQ'],                // Punta Arenas
    27: ['CJC'],                // Calama
    28: ['ZOS'],                // Osorno
    29: ['MHC'],                // Chiloé
    30: ['PNT', 'PUQ'],         // Puerto Natales
    31: ['MDE'],                // Medellín
    32: ['GIG', 'SDU'],         // Río de Janeiro (Galeão y Santos Dumont)
    33: ['GRU', 'CGH', 'VCP'],  // São Paulo (Guarulhos, Congonhas y Viracopos/BCP)
    34: ['SCL'],                // Curicó
    35: ['MVD'],                // Montevideo
    36: ['BCN']                 // Barcelona
};

const PICKUP_TYPES = [
    { value: 'MEET_AND_GREET', label: '🪧 Meet & Greet (Conductor espera con cartel)' },
    { value: 'CURBSIDE', label: '🚗 Calzada / Exterior (Andén de salida)' },
    { value: 'DESK', label: '🏢 Mostrador / Counter en el Terminal' },
    { value: 'HOST', label: '🧑‍✈️ Anfitrión / Coordinador en Hall' }
];

const normalizeText = (str) =>
    String(str || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const hasWholeWordMatch = (normalizedHaystack, rawKeyword) => {
    const kw = normalizeText(rawKeyword).trim();
    if (!kw || !normalizedHaystack) return false;
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i');
    return regex.test(normalizedHaystack);
};

const isCountryCompatible = (airportCountry, detectedCountriesSet) => {
    if (!detectedCountriesSet || detectedCountriesSet.size === 0) return true;
    const ac = normalizeText(airportCountry);
    return Array.from(detectedCountriesSet).some(dc =>
        ac.includes(dc) ||
        dc.includes(ac) ||
        (dc.includes('dominican') && ac.includes('dominican')) ||
        (dc.includes('rep') && ac.includes('dominican')) ||
        (dc.includes('brazil') && ac.includes('brasil')) ||
        (dc.includes('espana') && ac.includes('spain'))
    );
};

const BookingMeetingPointsManager = ({
    providerId = null,
    isAdmin = false,
    providerTariffs = [],
    bases = [],
    providerDetails = null
}) => {
    const [points, setPoints] = useState([]);
    const [providers, setProviders] = useState([]);
    const [internalTariffs, setInternalTariffs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const [filterCode, setFilterCode] = useState('');
    const [filterProvider, setFilterProvider] = useState('ALL');

    const [showModal, setShowModal] = useState(false);
    const [editingPoint, setEditingPoint] = useState(null);

    // Si estamos en modo proveedor y no vinieron tarifas por props, las cargamos para detectar sus aeropuertos habilitados
    useEffect(() => {
        if (!isAdmin && (!Array.isArray(providerTariffs) || providerTariffs.length === 0)) {
            getTariffs({ limit: 5000 })
                .then(data => setInternalTariffs(Array.isArray(data) ? data : []))
                .catch(() => {});
        }
    }, [isAdmin, providerTariffs]);

    const effectiveTariffs = useMemo(() => {
        if (Array.isArray(providerTariffs) && providerTariffs.length > 0) return providerTariffs;
        return internalTariffs;
    }, [providerTariffs, internalTariffs]);

    // Calcula únicamente los aeropuertos que el proveedor tiene disponibles según sus zonas/tarifas
    const availableAirports = useMemo(() => {
        if (isAdmin) {
            return PRESET_AIRPORTS;
        }

        const detectedCountries = new Set();
        const matchedAirportCodes = new Set();

        const provCountry = normalizeText(providerDetails?.country || '');
        const provCurrency = String(providerDetails?.currency_code || '').toUpperCase();
        if (provCountry) detectedCountries.add(provCountry);
        if (provCurrency === 'BRL') detectedCountries.add('brasil');
        if (provCurrency === 'PEN') detectedCountries.add('peru');
        if (provCurrency === 'ARS') detectedCountries.add('argentina');
        if (provCurrency === 'DOP') detectedCountries.add('dominican');
        if (provCurrency === 'CLP') detectedCountries.add('chile');
        if (provCurrency === 'UYU') detectedCountries.add('uruguay');
        if (provCurrency === 'COP') detectedCountries.add('colombia');
        if (provCurrency === 'MXN') detectedCountries.add('mexico');
        if (provCurrency === 'EUR') detectedCountries.add('spain');

        effectiveTariffs.forEach(t => {
            if (providerId && t.provider_id && Number(t.provider_id) !== Number(providerId)) return;

            const originBaseId = t.origin_zone?.base_id || t.origin_group?.base_id || t.origin_zone?.base?.id;
            const destBaseId = t.destination_zone?.base_id || t.destination_group?.base_id || t.destination_zone?.base?.id;

            // 1. Mapeo exacto por Base Operativa (BASE_ID_TO_AIRPORTS)
            let matchedByBaseId = false;
            [originBaseId, destBaseId].forEach(bid => {
                if (bid && BASE_ID_TO_AIRPORTS[Number(bid)]) {
                    BASE_ID_TO_AIRPORTS[Number(bid)].forEach(code => matchedAirportCodes.add(code));
                    matchedByBaseId = true;
                }
            });
            if (matchedByBaseId) return;

            // 2. Fallback por nombre de zona + país de la base (sólo si la base no está en BASE_ID_TO_AIRPORTS)
            const ozName = t.origin_zone?.name || t.origin_group?.name || '';
            const dzName = t.destination_zone?.name || t.destination_group?.name || '';
            const ozBaseName = t.origin_zone?.base?.name || '';
            const dzBaseName = t.destination_zone?.base?.name || '';
            const ozCountry = t.origin_zone?.base?.country || '';
            const dzCountry = t.destination_zone?.base?.country || '';

            const b1 = Array.isArray(bases) ? bases.find(b => b.id === originBaseId) : null;
            const b2 = Array.isArray(bases) ? bases.find(b => b.id === destBaseId) : null;

            const tariffText = [ozName, dzName, ozBaseName, dzBaseName, b1?.name, b2?.name]
                .filter(Boolean)
                .map(normalizeText)
                .join(' | ');

            const tariffCountries = new Set();
            [ozCountry, dzCountry, b1?.country, b2?.country].forEach(c => {
                if (c) {
                    const nc = normalizeText(c);
                    tariffCountries.add(nc);
                    detectedCountries.add(nc);
                }
            });

            if (tariffCountries.size === 0) {
                if (tariffText.includes('mendoza')) tariffCountries.add('argentina');
                else if (tariffText.includes('lima') || tariffText.includes('cusco')) tariffCountries.add('peru');
                else if (tariffText.includes('punta cana') || tariffText.includes('santo domingo')) tariffCountries.add('dominican');
                else if (tariffText.includes('sao paulo') || tariffText.includes('rio de janeiro') || tariffText.includes('guarulhos') || tariffText.includes('viracopos') || tariffText.includes('biracopos')) tariffCountries.add('brasil');
                else if (provCountry) tariffCountries.add(provCountry);
                else if (provCurrency === 'CLP') tariffCountries.add('chile');
            }

            if (tariffText) {
                PRESET_AIRPORTS.forEach(airport => {
                    if (!isCountryCompatible(airport.country, tariffCountries)) return;
                    if (airport.keywords.some(kw => hasWholeWordMatch(tariffText, kw))) {
                        matchedAirportCodes.add(airport.code);
                    }
                });
            }
        });

        if (matchedAirportCodes.size > 0) {
            return PRESET_AIRPORTS.filter(a => matchedAirportCodes.has(a.code));
        }

        // Si las zonas tenían nombres genéricos, filtrar por el país / moneda del proveedor
        if (detectedCountries.size > 0) {
            const matchedByCountry = PRESET_AIRPORTS.filter(airport =>
                isCountryCompatible(airport.country, detectedCountries)
            );
            if (matchedByCountry.length > 0) {
                return matchedByCountry;
            }
        }

        return PRESET_AIRPORTS;
    }, [isAdmin, effectiveTariffs, providerId, bases, providerDetails]);

    const [formData, setFormData] = useState({
        provider_id: providerId || null,
        location_code: 'SCL',
        custom_location_code: '',
        location_name: 'Aeropuerto Internacional Arturo Merino Benítez (Santiago)',
        terminal: '',
        type: 'AIRPORT',
        pickup_type: 'MEET_AND_GREET',
        title: '',
        instructions: '',
        pickup_images: [],
        language: 'es',
        is_active: true
    });

    const loadPoints = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (!isAdmin && providerId) {
                params.provider_id = providerId;
            }
            const data = await getBookingMeetingPoints(params);
            setPoints(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || 'Error al cargar puntos de encuentro.');
        } finally {
            setLoading(false);
        }
    }, [isAdmin, providerId]);

    useEffect(() => {
        loadPoints();
        if (isAdmin) {
            getProviders()
                .then(data => setProviders(Array.isArray(data) ? data : []))
                .catch(() => {});
        }
    }, [loadPoints, isAdmin]);

    const handleOpenNew = () => {
        const defaultAirport = availableAirports[0] || PRESET_AIRPORTS[0];
        setEditingPoint(null);
        setFormData({
            provider_id: isAdmin ? null : (providerId || null),
            location_code: defaultAirport.code,
            custom_location_code: '',
            location_name: defaultAirport.name,
            terminal: '',
            type: 'AIRPORT',
            pickup_type: 'MEET_AND_GREET',
            title: '',
            instructions: '',
            pickup_images: [],
            language: 'es',
            is_active: true
        });
        setShowModal(true);
    };

    const handleOpenEdit = (pt) => {
        const inAvailable = availableAirports.some(a => a.code === pt.location_code);
        const inAllPresets = PRESET_AIRPORTS.some(a => a.code === pt.location_code);
        setEditingPoint(pt);
        setFormData({
            provider_id: pt.provider_id,
            location_code: (inAvailable || inAllPresets) ? pt.location_code : 'CUSTOM',
            custom_location_code: (inAvailable || inAllPresets) ? '' : (pt.location_code || ''),
            location_name: pt.location_name || pt.location_match_text || '',
            terminal: pt.terminal || '',
            type: pt.type || 'AIRPORT',
            pickup_type: pt.pickup_type || 'MEET_AND_GREET',
            title: pt.title || '',
            instructions: pt.instructions || '',
            pickup_images: Array.isArray(pt.pickup_images) ? pt.pickup_images.slice(0, 3) : [],
            language: pt.language || 'es',
            is_active: pt.is_active !== false
        });
        setShowModal(true);
    };

    const handleUploadImageFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if ((formData.pickup_images || []).length >= 3) {
            alert('Booking.com permite un máximo de 3 fotografías por punto de encuentro.');
            return;
        }
        setUploadingImage(true);
        try {
            const res = await uploadBookingMeetingPointImage(file);
            if (res?.imageURL) {
                const defaultCaption = formData.title.trim() || `Punto de encuentro ${formData.location_code}`;
                setFormData(prev => ({
                    ...prev,
                    pickup_images: [
                        ...(prev.pickup_images || []),
                        {
                            imageURL: res.imageURL,
                            description: defaultCaption,
                            altText: defaultCaption
                        }
                    ].slice(0, 3)
                }));
            }
        } catch (err) {
            alert(err.message || 'No se pudo subir la imagen.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleUpdateImageCaption = (idx, value) => {
        setFormData(prev => {
            const next = [...(prev.pickup_images || [])];
            if (next[idx]) {
                next[idx] = {
                    ...next[idx],
                    description: value,
                    altText: value || prev.title || 'Punto de encuentro'
                };
            }
            return { ...prev, pickup_images: next };
        });
    };

    const handleRemoveImage = (idx) => {
        setFormData(prev => ({
            ...prev,
            pickup_images: (prev.pickup_images || []).filter((_, i) => i !== idx)
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const finalCode = (formData.location_code === 'CUSTOM' ? formData.custom_location_code : formData.location_code)
            .trim()
            .toUpperCase();

        if (!finalCode) {
            alert('Debes seleccionar un aeropuerto / código IATA.');
            return;
        }
        if (!formData.title.trim()) {
            alert('Debes ingresar el título corto del punto de encuentro (ej. Puerta 4 - Salida Internacional).');
            return;
        }
        if (!formData.instructions.trim()) {
            alert('Debes ingresar las instrucciones detalladas para el pasajero.');
            return;
        }
        if (formData.instructions.trim().length > 1000) {
            alert('Booking.com exige un máximo de 1000 caracteres en las instrucciones.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const payload = {
                provider_id: isAdmin
                    ? (formData.provider_id ? Number(formData.provider_id) : null)
                    : (providerId ? Number(providerId) : null),
                location_code: finalCode,
                location_match_text: formData.location_name.trim() || null,
                terminal: formData.terminal.trim() || null,
                type: formData.type || 'AIRPORT',
                pickup_type: formData.pickup_type || 'MEET_AND_GREET',
                title: formData.title.trim().slice(0, 255),
                instructions: formData.instructions.trim().slice(0, 1000),
                pickup_images: (formData.pickup_images || []).slice(0, 3).map(img => ({
                    imageURL: img.imageURL,
                    description: (img.description || formData.title.trim() || `Punto de encuentro ${finalCode}`).slice(0, 300),
                    altText: (img.altText || img.description || formData.title.trim() || `Imagen de ${finalCode}`).slice(0, 255)
                })),
                language: formData.language || 'es',
                is_active: Boolean(formData.is_active)
            };

            if (editingPoint) {
                await updateBookingMeetingPoint(editingPoint.id, payload);
                setSuccessMsg(`✅ Punto de encuentro para ${finalCode} actualizado y activo de inmediato.`);
            } else {
                await createBookingMeetingPoint(payload);
                setSuccessMsg(`✅ Punto de encuentro para ${finalCode} creado y activo de inmediato.`);
            }
            setShowModal(false);
            await loadPoints();
            setTimeout(() => setSuccessMsg(null), 5000);
        } catch (err) {
            alert(err.message || 'Error al guardar el punto de encuentro.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (pt) => {
        try {
            await updateBookingMeetingPoint(pt.id, { is_active: !pt.is_active });
            await loadPoints();
        } catch (err) {
            alert(err.message || 'No se pudo cambiar el estado del punto.');
        }
    };

    const handleDelete = async (pt) => {
        if (!window.confirm(`¿Eliminar el punto de encuentro "${pt.title}" (${pt.location_code})? Si no hay otro punto configurado para ese aeropuerto, Booking.com usará automáticamente las instrucciones por defecto de su Portal.`)) {
            return;
        }
        try {
            await deleteBookingMeetingPoint(pt.id);
            await loadPoints();
        } catch (err) {
            alert(err.message || 'Error al eliminar el punto de encuentro.');
        }
    };

    // Si estamos editando un punto cuyo IATA no estaba en availableAirports, lo incluimos temporalmente en las opciones del select
    const modalAirportOptions = useMemo(() => {
        if (!editingPoint || !editingPoint.location_code) return availableAirports;
        const exists = availableAirports.some(a => a.code === editingPoint.location_code);
        if (exists) return availableAirports;
        const fromPreset = PRESET_AIRPORTS.find(a => a.code === editingPoint.location_code);
        if (fromPreset) return [fromPreset, ...availableAirports];
        return availableAirports;
    }, [availableAirports, editingPoint]);

    const filteredPoints = points.filter(pt => {
        if (filterCode.trim()) {
            const q = filterCode.trim().toLowerCase();
            const matchCode = (pt.location_code || '').toLowerCase().includes(q);
            const matchName = (pt.location_name || '').toLowerCase().includes(q);
            const matchTitle = (pt.title || '').toLowerCase().includes(q);
            if (!matchCode && !matchName && !matchTitle) return false;
        }
        if (isAdmin && filterProvider !== 'ALL') {
            if (filterProvider === 'EMIGO_GENERAL') {
                if (pt.provider_id !== null) return false;
            } else if (String(pt.provider_id) !== String(filterProvider)) {
                return false;
            }
        }
        return true;
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Banner explicativo */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(0, 53, 128, 0.45), rgba(30, 64, 175, 0.25))',
                border: '1px solid rgba(59, 130, 246, 0.4)',
                borderRadius: '14px',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
            }}>
                <div style={{ maxWidth: '760px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <span style={{
                            background: '#003580',
                            color: '#fff',
                            padding: '3px 10px',
                            borderRadius: '999px',
                            fontSize: '0.72rem',
                            fontWeight: '800',
                            letterSpacing: '0.04em'
                        }}>
                            🏨 BOOKING.COM PICKUP API
                        </span>
                        <span style={{ color: '#93c5fd', fontSize: '0.8rem', fontWeight: '700' }}>
                            ⚡ Activación Inmediata
                        </span>
                        {!isAdmin && availableAirports.length > 0 && (
                            <span style={{
                                background: 'rgba(16, 185, 129, 0.2)',
                                border: '1px solid rgba(16, 185, 129, 0.45)',
                                color: '#6ee7b7',
                                padding: '2px 9px',
                                borderRadius: '999px',
                                fontSize: '0.72rem',
                                fontWeight: '700'
                            }}>
                                ✈️ Tus aeropuertos habilitados: {availableAirports.map(a => a.code).join(', ')}
                            </span>
                        )}
                    </div>
                    <h3 style={{ margin: '0 0 6px 0', color: '#f8fafc', fontSize: '1.1rem', fontWeight: '800' }}>
                        📍 Puntos de Encuentro (Pickup Instructions)
                    </h3>
                    <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.82rem', lineHeight: '1.45' }}>
                        Configura las instrucciones exactas donde tus conductores esperarán al pasajero en cada aeropuerto o estación de tus zonas operativas.
                        Al asignarse un viaje de Booking.com, EmiGo enviará automáticamente este punto de encuentro a la app del pasajero.
                        <strong> Si un aeropuerto no tiene punto configurado aquí, Booking.com mantendrá automáticamente las instrucciones por defecto de su Portal.</strong>
                    </p>
                </div>

                <button
                    onClick={handleOpenNew}
                    style={{
                        background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                        color: '#fff',
                        border: 'none',
                        padding: '11px 18px',
                        borderRadius: '10px',
                        fontWeight: '800',
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)',
                        whiteSpace: 'nowrap'
                    }}
                >
                    ➕ Nuevo Punto de Encuentro
                </button>
            </div>

            {successMsg && (
                <div style={{
                    background: 'rgba(16, 185, 129, 0.15)',
                    border: '1px solid rgba(16, 185, 129, 0.45)',
                    color: '#6ee7b7',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    fontSize: '0.88rem',
                    fontWeight: '700'
                }}>
                    {successMsg}
                </div>
            )}

            {error && (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.45)',
                    color: '#fca5a5',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    fontSize: '0.88rem'
                }}>
                    ⚠️ {error}
                </div>
            )}

            {/* Barra de Filtros */}
            <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                alignItems: 'center',
                background: 'rgba(15, 23, 42, 0.65)',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.08)'
            }}>
                <input
                    type="text"
                    placeholder="🔍 Buscar por IATA, aeropuerto o título..."
                    value={filterCode}
                    onChange={(e) => setFilterCode(e.target.value)}
                    style={{
                        flex: 1,
                        minWidth: '240px',
                        background: '#0f172a',
                        border: '1px solid #334155',
                        color: '#f8fafc',
                        padding: '9px 14px',
                        borderRadius: '8px',
                        fontSize: '0.85rem'
                    }}
                />
                {isAdmin && (
                    <select
                        value={filterProvider}
                        onChange={(e) => setFilterProvider(e.target.value)}
                        style={{
                            background: '#0f172a',
                            border: '1px solid #334155',
                            color: '#f8fafc',
                            padding: '9px 14px',
                            borderRadius: '8px',
                            fontSize: '0.85rem'
                        }}
                    >
                        <option value="ALL">🏢 Todos (General EmiGo + Proveedores)</option>
                        <option value="EMIGO_GENERAL">🌐 Solo Puntos Generales EmiGo (Fallback)</option>
                        {providers.map(p => (
                            <option key={p.id} value={p.id}>🚐 {p.name}</option>
                        ))}
                    </select>
                )}
                <button
                    onClick={loadPoints}
                    style={{
                        background: '#1e293b',
                        color: '#cbd5e1',
                        border: '1px solid #334155',
                        padding: '9px 14px',
                        borderRadius: '8px',
                        fontSize: '0.84rem',
                        cursor: 'pointer',
                        fontWeight: '600'
                    }}
                >
                    🔄 Actualizar
                </button>
            </div>

            {/* Listado de tarjetas */}
            {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                    Cargando puntos de encuentro...
                </div>
            ) : filteredPoints.length === 0 ? (
                <div style={{
                    background: 'rgba(15, 23, 42, 0.5)',
                    border: '1px dashed rgba(148, 163, 184, 0.3)',
                    borderRadius: '14px',
                    padding: '36px 20px',
                    textAlign: 'center',
                    color: '#94a3b8'
                }}>
                    <div style={{ fontSize: '2.2rem', marginBottom: '8px' }}>📍</div>
                    <div style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '1rem', marginBottom: '4px' }}>
                        Aún no hay puntos de encuentro personalizados aquí
                    </div>
                    <div style={{ fontSize: '0.84rem', maxWidth: '520px', margin: '0 auto 16px auto' }}>
                        Mientras no configures un punto para un aeropuerto, Booking.com enviará las instrucciones por defecto que tengas en su Portal. Crea uno cuando quieras personalizar tu punto de recogida.
                    </div>
                    <button
                        onClick={handleOpenNew}
                        style={{
                            background: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            padding: '9px 16px',
                            borderRadius: '8px',
                            fontWeight: '700',
                            cursor: 'pointer'
                        }}
                    >
                        ➕ Crear primer Punto de Encuentro
                    </button>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                    gap: '14px'
                }}>
                    {filteredPoints.map(pt => {
                        const isGeneral = pt.provider_id === null;
                        const canModify = isAdmin || (providerId && Number(pt.provider_id) === Number(providerId));
                        return (
                            <div
                                key={pt.id}
                                style={{
                                    background: pt.is_active ? 'rgba(15, 23, 42, 0.85)' : 'rgba(15, 23, 42, 0.45)',
                                    border: pt.is_active
                                        ? (isGeneral ? '1px solid rgba(56, 189, 248, 0.45)' : '1px solid rgba(59, 130, 246, 0.45)')
                                        : '1px solid rgba(100, 116, 139, 0.25)',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    opacity: pt.is_active ? 1 : 0.65
                                }}
                            >
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                            <span style={{
                                                background: '#1d4ed8',
                                                color: '#fff',
                                                fontWeight: '900',
                                                fontSize: '0.88rem',
                                                padding: '4px 10px',
                                                borderRadius: '8px',
                                                letterSpacing: '0.05em'
                                            }}>
                                                ✈️ {pt.location_code}
                                            </span>
                                            {pt.terminal ? (
                                                <span style={{
                                                    background: 'rgba(245, 158, 11, 0.18)',
                                                    color: '#fcd34d',
                                                    border: '1px solid rgba(245, 158, 11, 0.4)',
                                                    fontSize: '0.73rem',
                                                    fontWeight: '700',
                                                    padding: '3px 8px',
                                                    borderRadius: '6px'
                                                }}>
                                                    Terminal: {pt.terminal}
                                                </span>
                                            ) : (
                                                <span style={{
                                                    background: 'rgba(148, 163, 184, 0.15)',
                                                    color: '#cbd5e1',
                                                    fontSize: '0.72rem',
                                                    padding: '3px 8px',
                                                    borderRadius: '6px'
                                                }}>
                                                    Todos los terminales
                                                </span>
                                            )}
                                            <span style={{
                                                background: 'rgba(139, 92, 246, 0.18)',
                                                color: '#c4b5fd',
                                                border: '1px solid rgba(139, 92, 246, 0.35)',
                                                fontSize: '0.7rem',
                                                fontWeight: '700',
                                                padding: '3px 8px',
                                                borderRadius: '6px'
                                            }}>
                                                {pt.pickup_type === 'CURBSIDE' ? '🚗 Calzada'
                                                    : pt.pickup_type === 'DESK' ? '🏢 Counter'
                                                    : pt.pickup_type === 'HOST' ? '🧑‍✈️ Anfitrión'
                                                    : '🪧 Meet & Greet'}
                                            </span>
                                        </div>

                                        <span style={{
                                            background: pt.is_active ? 'rgba(16, 185, 129, 0.18)' : 'rgba(100, 116, 139, 0.25)',
                                            color: pt.is_active ? '#34d399' : '#94a3b8',
                                            fontSize: '0.7rem',
                                            fontWeight: '800',
                                            padding: '3px 8px',
                                            borderRadius: '999px'
                                        }}>
                                            {pt.is_active ? '● ACTIVO' : '○ PAUSADO'}
                                        </span>
                                    </div>

                                    {(pt.location_name || pt.location_match_text) && (
                                        <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginBottom: '6px' }}>
                                            {pt.location_name || pt.location_match_text}
                                        </div>
                                    )}

                                    <div style={{
                                        fontSize: '0.95rem',
                                        fontWeight: '800',
                                        color: '#f8fafc',
                                        marginBottom: '6px'
                                    }}>
                                        📍 {pt.title}
                                    </div>

                                    <div style={{
                                        background: 'rgba(2, 6, 23, 0.55)',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                        borderRadius: '8px',
                                        padding: '10px 12px',
                                        fontSize: '0.82rem',
                                        color: '#cbd5e1',
                                        lineHeight: '1.45',
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                        {pt.instructions}
                                    </div>

                                    {Array.isArray(pt.pickup_images) && pt.pickup_images.length > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#93c5fd', marginBottom: '6px' }}>
                                                📷 Fotografías adjuntas para el pasajero ({pt.pickup_images.length}/3):
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                {pt.pickup_images.map((img, i) => (
                                                    <a
                                                        key={i}
                                                        href={img.imageURL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={img.description || 'Ver imagen completa'}
                                                        style={{
                                                            display: 'block',
                                                            width: '78px',
                                                            height: '58px',
                                                            borderRadius: '8px',
                                                            overflow: 'hidden',
                                                            border: '1px solid rgba(59, 130, 246, 0.45)',
                                                            background: '#020617',
                                                            position: 'relative'
                                                        }}
                                                    >
                                                        <img
                                                            src={img.imageURL}
                                                            alt={img.altText || pt.title}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    paddingTop: '8px',
                                    borderTop: '1px solid rgba(255,255,255,0.07)',
                                    gap: '8px',
                                    flexWrap: 'wrap'
                                }}>
                                    <span style={{
                                        fontSize: '0.73rem',
                                        fontWeight: '700',
                                        color: isGeneral ? '#38bdf8' : '#a78bfa'
                                    }}>
                                        {isGeneral
                                            ? '🌐 Punto General EmiGo (Fallback)'
                                            : `🚐 Proveedor: ${pt.provider_name || `#${pt.provider_id}`}`}
                                    </span>

                                    {canModify ? (
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                onClick={() => handleToggleActive(pt)}
                                                style={{
                                                    background: 'rgba(51, 65, 85, 0.7)',
                                                    color: '#e2e8f0',
                                                    border: 'none',
                                                    padding: '5px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '0.74rem',
                                                    cursor: 'pointer',
                                                    fontWeight: '600'
                                                }}
                                            >
                                                {pt.is_active ? '⏸ Pausar' : '▶️ Activar'}
                                            </button>
                                            <button
                                                onClick={() => handleOpenEdit(pt)}
                                                style={{
                                                    background: 'rgba(37, 99, 235, 0.25)',
                                                    color: '#93c5fd',
                                                    border: '1px solid rgba(59, 130, 246, 0.4)',
                                                    padding: '5px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '0.74rem',
                                                    cursor: 'pointer',
                                                    fontWeight: '700'
                                                }}
                                            >
                                                ✏️ Editar
                                            </button>
                                            <button
                                                onClick={() => handleDelete(pt)}
                                                style={{
                                                    background: 'rgba(239, 68, 68, 0.15)',
                                                    color: '#fca5a5',
                                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                                    padding: '5px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '0.74rem',
                                                    cursor: 'pointer',
                                                    fontWeight: '700'
                                                }}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    ) : (
                                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                                            Punto global de respaldo EmiGo
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal Crear / Editar */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(2, 6, 23, 0.8)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px'
                }}>
                    <div style={{
                        background: '#0f172a',
                        border: '1px solid #334155',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '620px',
                        maxHeight: '92vh',
                        overflowY: 'auto',
                        padding: '22px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '1.1rem', fontWeight: '800' }}>
                                {editingPoint ? '✏️ Editar Punto de Encuentro Booking.com' : '➕ Nuevo Punto de Encuentro Booking.com'}
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#94a3b8',
                                    fontSize: '1.2rem',
                                    cursor: 'pointer'
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {isAdmin && (
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '5px' }}>
                                        Asignación (General EmiGo o Proveedor Específico)
                                    </label>
                                    <select
                                        value={formData.provider_id || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            provider_id: e.target.value ? Number(e.target.value) : null
                                        })}
                                        style={{
                                            width: '100%',
                                            background: '#1e293b',
                                            border: '1px solid #334155',
                                            color: '#f8fafc',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.86rem'
                                        }}
                                    >
                                        <option value="">🌐 General EmiGo (Se usa si el proveedor no tiene punto propio)</option>
                                        {providers.map(p => (
                                            <option key={p.id} value={p.id}>🚐 Proveedor: {p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {!isAdmin && (
                                <div style={{
                                    background: 'rgba(37, 99, 235, 0.12)',
                                    border: '1px solid rgba(59, 130, 246, 0.35)',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    fontSize: '0.78rem',
                                    color: '#93c5fd'
                                }}>
                                    ✈️ Mostrando únicamente los aeropuertos habilitados en tus zonas operativas ({modalAirportOptions.length}).
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '5px' }}>
                                        Aeropuerto Habilitado (IATA) *
                                    </label>
                                    <select
                                        value={formData.location_code}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const preset = PRESET_AIRPORTS.find(a => a.code === val);
                                            setFormData({
                                                ...formData,
                                                location_code: val,
                                                location_name: preset ? preset.name : formData.location_name
                                            });
                                        }}
                                        style={{
                                            width: '100%',
                                            background: '#1e293b',
                                            border: '1px solid #334155',
                                            color: '#f8fafc',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {modalAirportOptions.map(a => (
                                            <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                                        ))}
                                        {isAdmin && (
                                            <option value="CUSTOM">✍️ Otro código IATA / Estación...</option>
                                        )}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '5px' }}>
                                        Modalidad de Recogida (Booking pickupType) *
                                    </label>
                                    <select
                                        value={formData.pickup_type || 'MEET_AND_GREET'}
                                        onChange={(e) => setFormData({ ...formData, pickup_type: e.target.value })}
                                        style={{
                                            width: '100%',
                                            background: '#1e293b',
                                            border: '1px solid #334155',
                                            color: '#f8fafc',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {PICKUP_TYPES.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {isAdmin && formData.location_code === 'CUSTOM' && (
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '5px' }}>
                                        Código IATA o Identificador (ej. MIA, MAD, BCN) *
                                    </label>
                                    <input
                                        type="text"
                                        maxLength={10}
                                        placeholder="Ej: MIA"
                                        value={formData.custom_location_code}
                                        onChange={(e) => setFormData({ ...formData, custom_location_code: e.target.value.toUpperCase() })}
                                        style={{
                                            width: '100%',
                                            background: '#1e293b',
                                            border: '1px solid #334155',
                                            color: '#f8fafc',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.86rem'
                                        }}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '5px' }}>
                                        Nombre descriptivo del Aeropuerto / Estación
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ej: Aeropuerto Internacional Arturo Merino Benítez"
                                        value={formData.location_name}
                                        onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                                        style={{
                                            width: '100%',
                                            background: '#1e293b',
                                            border: '1px solid #334155',
                                            color: '#f8fafc',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '5px' }}>
                                        Terminal / Ubicación (ej. T1, T2)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Vacío = Todos (o T1, T2)"
                                        value={formData.terminal}
                                        onChange={(e) => setFormData({ ...formData, terminal: e.target.value })}
                                        style={{
                                            width: '100%',
                                            background: '#1e293b',
                                            border: '1px solid #334155',
                                            color: '#f8fafc',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1' }}>
                                        Título / Referencia del Punto de Encuentro (visible en el voucher de Booking) *
                                    </label>
                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                        {formData.title.length}/150
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    maxLength={150}
                                    required
                                    placeholder="Ej: Puerta 4 - Salida Internacional (Frente a Cafetería Starbucks)"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    style={{
                                        width: '100%',
                                        background: '#1e293b',
                                        border: '1px solid #334155',
                                        color: '#f8fafc',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        fontSize: '0.86rem'
                                    }}
                                />
                            </div>

                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1' }}>
                                        Instrucciones paso a paso para el pasajero (Máx. 1000 caracteres) *
                                    </label>
                                    <span style={{
                                        fontSize: '0.74rem',
                                        fontWeight: '700',
                                        color: formData.instructions.length > 900 ? '#f87171' : '#38bdf8'
                                    }}>
                                        {formData.instructions.length}/1000
                                    </span>
                                </div>
                                <textarea
                                    rows={4}
                                    maxLength={1000}
                                    required
                                    placeholder="Ej: Después de retirar tu equipaje y pasar el control de Aduana, sal por la Puerta 4. Tu conductor te esperará sosteniendo un cartel con tu nombre."
                                    value={formData.instructions}
                                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                                    style={{
                                        width: '100%',
                                        background: '#1e293b',
                                        border: '1px solid #334155',
                                        color: '#f8fafc',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            {/* Fotografías del Punto de Encuentro (pickupImages de Booking.com - Hasta 3 fotos) */}
                            <div style={{
                                background: 'rgba(15, 23, 42, 0.75)',
                                border: '1px solid rgba(59, 130, 246, 0.35)',
                                borderRadius: '10px',
                                padding: '12px 14px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                                    <div>
                                        <div style={{ fontSize: '0.82rem', fontWeight: '800', color: '#e2e8f0' }}>
                                            📷 Fotografías del Punto de Encuentro ({(formData.pickup_images || []).length}/3)
                                        </div>
                                        <div style={{ fontSize: '0.73rem', color: '#94a3b8' }}>
                                            Booking.com permite enviar hasta 3 fotos referenciales del lugar exacto donde esperará el conductor.
                                        </div>
                                    </div>

                                    {(formData.pickup_images || []).length < 3 && (
                                        <label style={{
                                            background: uploadingImage ? '#334155' : 'rgba(37, 99, 235, 0.25)',
                                            color: '#93c5fd',
                                            border: '1px solid rgba(59, 130, 246, 0.5)',
                                            padding: '7px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.78rem',
                                            fontWeight: '800',
                                            cursor: uploadingImage ? 'wait' : 'pointer',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {uploadingImage ? '⏳ Subiendo foto...' : '📤 Subir Fotografía'}
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                onChange={handleUploadImageFile}
                                                disabled={uploadingImage}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    )}
                                </div>

                                {(formData.pickup_images || []).length === 0 ? (
                                    <div style={{
                                        fontSize: '0.76rem',
                                        color: '#64748b',
                                        textAlign: 'center',
                                        padding: '10px',
                                        border: '1px dashed rgba(100, 116, 139, 0.3)',
                                        borderRadius: '8px'
                                    }}>
                                        Sin fotografías adjuntas (opcional). Puedes subir hasta 3 imágenes en formato JPG, PNG o WEBP.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                                        {(formData.pickup_images || []).map((img, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: 'flex',
                                                    gap: '10px',
                                                    alignItems: 'center',
                                                    background: '#1e293b',
                                                    border: '1px solid #334155',
                                                    borderRadius: '8px',
                                                    padding: '8px'
                                                }}
                                            >
                                                <img
                                                    src={img.imageURL}
                                                    alt={img.altText || `Foto ${idx + 1}`}
                                                    style={{
                                                        width: '72px',
                                                        height: '54px',
                                                        objectFit: 'cover',
                                                        borderRadius: '6px',
                                                        border: '1px solid rgba(255,255,255,0.1)',
                                                        flexShrink: 0
                                                    }}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', marginBottom: '3px' }}>
                                                        Descripción / Pie de foto para el pasajero (Foto #{idx + 1}):
                                                    </label>
                                                    <input
                                                        type="text"
                                                        maxLength={250}
                                                        placeholder="Ej: Vista de la salida puerta 4 frente a cafetería Starbucks"
                                                        value={img.description || ''}
                                                        onChange={(e) => handleUpdateImageCaption(idx, e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            background: '#0f172a',
                                                            border: '1px solid #334155',
                                                            color: '#f8fafc',
                                                            padding: '6px 10px',
                                                            borderRadius: '6px',
                                                            fontSize: '0.78rem'
                                                        }}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage(idx)}
                                                    title="Quitar fotografía"
                                                    style={{
                                                        background: 'rgba(239, 68, 68, 0.18)',
                                                        color: '#fca5a5',
                                                        border: '1px solid rgba(239, 68, 68, 0.35)',
                                                        padding: '6px 9px',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '0.78rem',
                                                        fontWeight: '700'
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                background: 'rgba(16, 185, 129, 0.1)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                color: '#e2e8f0',
                                fontSize: '0.84rem'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                                <span>
                                    <strong>Activar de inmediato</strong> (se enviará automáticamente a Booking.com en los próximos viajes de este origen)
                                </span>
                            </label>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        background: '#1e293b',
                                        color: '#cbd5e1',
                                        border: '1px solid #334155',
                                        padding: '10px 16px',
                                        borderRadius: '8px',
                                        fontWeight: '700',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || uploadingImage}
                                    style={{
                                        background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        fontWeight: '800',
                                        cursor: (saving || uploadingImage) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {saving ? 'Guardando...' : '💾 Guardar Punto de Encuentro'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingMeetingPointsManager;
